import { useParams, Link } from "wouter";
import { useState, useEffect } from "react";
import { keepPreviousData } from "@tanstack/react-query";
import { trpc } from "../lib/trpc";
import { Button, Card, Badge, Spinner, Select } from "../components/ui";
import { fmtDateTime } from "../lib/format";
import { ApplyProgress } from "../components/ApplyProgress";
import { useProgressStream } from "../hooks/useSSE";
import { JobProgress } from "../components/JobProgress";

const eur = (n: unknown) => (typeof n === "number" ? `€ ${n.toFixed(2)}` : String(n ?? "—"));
const activeWord = (v: unknown) => (v === true ? "Active" : v === false ? "Inactive" : "—");

type OpLite = {
  id: number;
  opType: string;
  diff: unknown;
  entityKey: string;
  method: string;
  endpoint: string;
  status: string;
  target: string;
  lastError: string | null;
  viewUrl?: string | null; // parsed from this op's OWN endpoint — always correct, even when duplicates share a code
};

/** Human-readable rendering of one operation's change. */
type PriceDelta = { from: number; to: number; pct: number | null };
function PriceChange({ label, d }: { label: string; d: PriceDelta }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="text-slate-500">{label}</span>
      <span className="line-through decoration-slate-300">{eur(d.from)}</span>
      <span className="text-slate-400">→</span>
      <span className="font-medium text-slate-900">{eur(d.to)}</span>
      {d.pct != null && <Badge color={Math.abs(d.pct) >= 20 ? "amber" : "gray"}>{d.pct > 0 ? "+" : ""}{d.pct}%</Badge>}
    </span>
  );
}

/**
 * Item identity cell. For Lightspeed it shows BOTH SKUs — the SUPPLIER (PBS) SKU and the SHOP code
 * (the Lightspeed article code, which equals the ExactOnline ItemCode). `fallback` is the supplier SKU/name
 * resolved server-side for ops whose diff predates carrying them (older change sets).
 */
function ItemCell({ op, fallback }: { op: OpLite; fallback?: { sku?: string | null; desc?: string | null; url?: string | null; itemCode?: string | null } }) {
  const d = (parseMaybe(op.diff) ?? {}) as { itemCode?: string; matchCount?: number; sku?: string; articleCode?: string; desc?: string };
  if (op.target === "lightspeed") {
    const sku = d.sku ?? fallback?.sku ?? null;      // supplier (PBS) SKU
    const article = d.articleCode ?? op.entityKey;   // shop code = ExactOnline ItemCode
    const desc = d.desc ?? fallback?.desc ?? null;
    const lsUrl = op.viewUrl ?? fallback?.url ?? null; // per-op deep link (falls back to the legacy shared lookup for older rows)
    return (
      <div className="space-y-0.5 leading-tight">
        <div className="flex items-baseline gap-1.5">
          <span className="w-14 shrink-0 whitespace-nowrap text-[10px] uppercase tracking-wide text-slate-400">supplier</span>
          {sku ? (
            <Link href={`/products/${sku}`} className="select-all whitespace-nowrap font-mono text-xs font-medium text-indigo-600 hover:underline" title="PBS supplier SKU — open this product">{sku}</Link>
          ) : <span className="text-xs text-slate-300">—</span>}
        </div>
        <div className="flex items-baseline gap-1.5">
          <span className="w-14 shrink-0 whitespace-nowrap text-[10px] uppercase tracking-wide text-slate-400">shop</span>
          {lsUrl ? (
            <a href={lsUrl} target="_blank" rel="noopener noreferrer" className="select-all whitespace-nowrap font-mono text-xs font-medium text-indigo-600 hover:underline" title="Open this product in the Lightspeed admin ↗">{article} ↗</a>
          ) : (
            <span className="select-all whitespace-nowrap font-mono text-xs text-slate-600" title="Lightspeed article code = ExactOnline ItemCode">{article}</span>
          )}
        </div>
        {desc && <div className="text-[11px] leading-snug text-slate-500" title={desc}>{desc}</div>}
      </div>
    );
  }
  // ExactOnline: entityKey IS the PBS (supplier) SKU. Show it, the ExactOnline item code (from the diff or
  // resolved fallback, linked to ExactOnline when we have the GUID), and the product name — mirroring the
  // Lightspeed row so both change sets read the same way.
  const itemCode = d.itemCode ?? fallback?.itemCode ?? null;
  const desc = d.desc ?? fallback?.desc ?? null;
  const exUrl = op.viewUrl ?? fallback?.url ?? null; // per-op deep link (falls back to the legacy shared lookup for older rows)
  return (
    <div className="space-y-0.5 leading-tight">
      <div className="flex items-baseline gap-1.5">
        <span className="w-14 shrink-0 whitespace-nowrap text-[10px] uppercase tracking-wide text-slate-400">supplier</span>
        <Link href={`/products/${op.entityKey}`} className="select-all whitespace-nowrap font-mono text-xs font-medium text-indigo-600 hover:underline" title="PBS supplier SKU — open this product">{op.entityKey}</Link>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="w-14 shrink-0 whitespace-nowrap text-[10px] uppercase tracking-wide text-slate-400">item</span>
        {itemCode ? (
          exUrl ? (
            <a href={exUrl} target="_blank" rel="noopener noreferrer" className="select-all whitespace-nowrap font-mono text-xs font-medium text-indigo-600 hover:underline" title="Open this item in ExactOnline ↗">{itemCode} ↗</a>
          ) : (
            <span className="select-all whitespace-nowrap font-mono text-xs text-slate-600" title="ExactOnline ItemCode">{itemCode}</span>
          )
        ) : <span className="text-xs text-slate-300">—</span>}
        {(d.matchCount ?? 0) > 1 && <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] text-amber-700" title="multiple ExactOnline items match this PBS code">{d.matchCount}×</span>}
      </div>
      {desc && <div className="text-[11px] leading-snug text-slate-500" title={desc}>{desc}</div>}
    </div>
  );
}

function DiffCell({ op }: { op: OpLite }) {
  const d = (parseMaybe(op.diff) ?? {}) as { kind?: string; field?: string; from?: unknown; to?: unknown; deltaPct?: number; itemCode?: string; warnings?: string[]; fields?: string[]; action?: string; sales?: PriceDelta; purchase?: PriceDelta; reason?: string; lsCategory?: string; brand?: string | null; price?: number; tracking?: string; changes?: FieldChangeT[] };

  if (d.kind === "update" && (d.fields?.length || d.action)) {
    // EndDate is the active-state change — represented by the action verb, not listed as a field.
    // Price fields render as from→to+% (below); other fields are summarised as "sync …".
    const labels: Record<string, string> = { Description: "description", SupplierItemCode: "supplier code" };
    const others = Array.from(new Set((d.fields ?? []).filter((f) => !["EndDate", "Status", "SalesPrice", "PurchasePrice", "StandardSalesPrice"].includes(f)).map((f) => labels[f] ?? f)));
    return (
      <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-slate-700">
        {d.action && <span className="font-medium text-slate-900">{d.action}</span>}
        {d.sales && <PriceChange label="sales" d={d.sales} />}
        {d.purchase && <PriceChange label="purchase" d={d.purchase} />}
        {others.length ? <span>sync {others.join(", ")}</span> : null}
      </span>
    );
  }
  if (op.opType === "create" || d.kind === "create") {
    const warnings = d.warnings ?? [];
    const warnBadge = warnings.length > 0 && <span className="ml-1.5" title={warnings.join("\n")}><Badge color="amber">{warnings.length} warning{warnings.length > 1 ? "s" : ""}</Badge></span>;
    if (op.target === "lightspeed") {
      return (
        <span className="flex flex-wrap items-center gap-x-2 text-slate-700">
          <span className="font-medium text-slate-900">Create on Lightspeed</span>
          {d.lsCategory && <span className="text-xs text-slate-500">cat: {d.lsCategory}</span>}
          {d.brand && <span className="text-xs text-slate-500">· {d.brand}</span>}
          {typeof d.price === "number" && <span className="text-xs">· {eur(d.price)}</span>}
          {warnBadge}
        </span>
      );
    }
    return (
      <span className="text-slate-700">
        Create item + link supplier
        {d.itemCode ? <> <span className="font-mono text-xs font-semibold text-slate-800">{d.itemCode}</span></> : null}
        {warnBadge}
      </span>
    );
  }
  // A coalesced Lightspeed op carries several field changes in `changes` (one payload = one API call);
  // render each. Older/single-field ops fall through to the single renderer below.
  if (Array.isArray(d.changes)) {
    return (
      <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {d.changes.map((c, i) => <FieldDiff key={i} d={c} />)}
      </span>
    );
  }
  return <FieldDiff d={d as FieldChangeT} />;
}

type FieldChangeT = { field?: string; from?: unknown; to?: unknown; deltaPct?: number | null; tracking?: string; reason?: string };

/** Render a single field change (from → to), shared by single-field ops and each entry of a coalesced op. */
function FieldDiff({ d }: { d: FieldChangeT }) {
  if (d.field === "price") {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className="text-slate-500">Price</span>
        <span className="line-through decoration-slate-300">{eur(d.from)}</span>
        <span className="text-slate-400">→</span>
        <span className="font-medium text-slate-900">{eur(d.to)}</span>
        {d.deltaPct != null && <Badge color={Math.abs(d.deltaPct) >= 20 ? "amber" : "gray"}>{d.deltaPct > 0 ? "+" : ""}{d.deltaPct}%</Badge>}
      </span>
    );
  }
  if (d.field === "purchase") {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className="text-slate-500">Purchase price</span>
        <span className="line-through decoration-slate-300">{eur(d.from)}</span>
        <span className="text-slate-400">→</span>
        <span className="font-medium text-slate-900">{eur(d.to)}</span>
      </span>
    );
  }
  if (d.field === "supplier") {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className="text-slate-500">Supplier</span>
        <span className="text-slate-400">{String(d.from ?? "—")} →</span>
        <span className="font-medium text-slate-900">{String(d.to ?? "")}</span>
      </span>
    );
  }
  if (d.field === "name") {
    return (
      <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
        <span className="text-slate-500">Name</span>
        <span className="text-slate-400 line-through decoration-slate-300">{String(d.from ?? "—")}</span>
        <span className="text-slate-400">→</span>
        <span className="font-medium text-slate-900">{String(d.to ?? "")}</span>
      </span>
    );
  }
  if (d.field === "stock") {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className="text-slate-500">Stock</span>
        <span className="line-through decoration-slate-300">{String(d.from ?? 0)}</span>
        <span className="text-slate-400">→</span>
        <span className="font-medium text-slate-900">{String(d.to ?? 0)}</span>
        {d.tracking === "enabled" && <Badge color="blue">track stock</Badge>}
      </span>
    );
  }
  if (d.field === "ean") {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className="text-slate-500">EAN</span>
        <span className="font-mono text-xs text-slate-400 line-through decoration-slate-300">{String(d.from ?? "—")}</span>
        <span className="text-slate-400">→</span>
        <span className="font-mono text-xs font-medium text-slate-900">{String(d.to ?? "")}</span>
      </span>
    );
  }
  if (d.field === "weight") {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className="text-slate-500">Weight</span>
        <span className="line-through decoration-slate-300">{String(d.from ?? 0)} g</span>
        <span className="text-slate-400">→</span>
        <span className="font-medium text-slate-900">{String(d.to ?? 0)} g</span>
      </span>
    );
  }
  if (d.field === "brand") {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className="text-slate-500">Brand</span>
        <span className="text-slate-400">{String(d.from ?? "—")} →</span>
        <span className="font-medium text-slate-900">{String(d.to ?? "")}</span>
      </span>
    );
  }
  if (d.field === "category") {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className="text-slate-500">Category</span>
        <Badge color="green">+ {String(d.to ?? "")}</Badge>
      </span>
    );
  }
  if (d.field === "visibility") {
    const lsVisible = (x: unknown) => x === "auto" || x === "visible";
    return (
      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="inline-flex items-center gap-1.5">
          <span className="text-slate-500">{String(d.from ?? "—")}</span>
          <span className="text-slate-400">→</span>
          <Badge color={lsVisible(d.to) ? "green" : "gray"}>{lsVisible(d.to) ? "visible" : "hidden"}</Badge>
        </span>
        {d.reason && <span className="text-xs text-slate-500">· {d.reason}</span>}
      </span>
    );
  }
  if (d.field === "active") {
    const to = d.to === true;
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className="text-slate-500">{activeWord(d.from)}</span>
        <span className="text-slate-400">→</span>
        <Badge color={to ? "green" : "gray"}>{activeWord(d.to)}</Badge>
      </span>
    );
  }
  if (d.field) {
    return <span className="text-slate-700">{d.field}: {String(d.from)} → {String(d.to)}</span>;
  }
  return <span className="text-slate-400">—</span>;
}

function parseMaybe(v: unknown): unknown {
  if (typeof v === "string") {
    try {
      return JSON.parse(v);
    } catch {
      return v;
    }
  }
  return v;
}

/** Render one field's value, bold/indigo so it's obvious it's the value being written. */
function FieldValue({ value }: { value: unknown }) {
  if (value === "" || value === null || value === undefined) return <span className="italic text-slate-400">— linked at apply time</span>;
  if (typeof value === "boolean") return <span className="font-semibold text-purple-700">{String(value)}</span>;
  if (typeof value === "number") return <span className="font-semibold text-blue-700">{value}</span>;
  // Nested objects (e.g. a Lightspeed step body { product: {…} }) — show JSON, not "[object Object]".
  if (typeof value === "object") return <span className="whitespace-pre-wrap break-all font-mono text-xs font-semibold text-indigo-700">{JSON.stringify(value)}</span>;
  const str = String(value);
  // multi-line (e.g. Notes) — preserve newlines
  return <span className="whitespace-pre-wrap font-semibold text-indigo-700">{str}</span>;
}

type Change = { field: string; from: unknown; to: unknown };
type Step = { entity: string; label: string; method: string; endpoint: string; body: Record<string, unknown>; changes?: Change[]; reason?: string };

/** A single create/update step. `state` reflects what already happened on a prior (partial) apply. */
function StepCard({ step, index, state }: { step: Step; index: number; state?: "done" | "skipped" | "failed" | "pending" }) {
  const entries = Object.entries(step.body ?? {});
  const isUpdate = step.method !== "POST";
  const done = state === "done" || state === "skipped";
  const numBg = state === "done" || state === "skipped" ? "bg-emerald-600" : state === "failed" ? "bg-red-600" : isUpdate ? "bg-blue-600" : "bg-emerald-600";
  const badge =
    state === "done" ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700">✓ done</span>
    : state === "skipped" ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700">✓ already in sync</span>
    : state === "failed" ? <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-700">✗ failed here</span>
    : state === "pending" ? <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">pending</span>
    : null;
  return (
    <div className={`rounded-xl border ${state === "failed" ? "border-red-200" : "border-slate-200"} ${done ? "opacity-70" : ""}`}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-slate-100 bg-slate-50 px-4 py-2">
        <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white ${numBg}`}>{done ? "✓" : index + 1}</span>
        <span className="shrink-0 whitespace-nowrap text-sm font-semibold text-slate-800">{step.label}</span>
        {badge}
        <span className="ml-auto max-w-full break-all rounded bg-white px-1.5 py-0.5 font-mono text-[11px] text-slate-600 ring-1 ring-slate-200">
          <span className={isUpdate ? "text-blue-700" : "text-emerald-700"}>{step.method}</span> {step.endpoint}
        </span>
      </div>
      {done ? null : (<>

      {step.reason && (
        <div className="border-b border-slate-100 bg-blue-50/60 px-4 py-2 text-xs text-slate-700">
          <span className="font-semibold text-blue-700">Why: </span>{step.reason}
        </div>
      )}

      {step.changes && step.changes.length > 0 && (
        <div className="border-b border-slate-100 px-4 py-2">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">What changes</div>
          {step.changes.map((c, i) => (
            <div key={i} className="flex items-center gap-2 py-0.5 text-sm">
              <span className="w-24 shrink-0 font-mono text-xs text-slate-500">{c.field}</span>
              <span className="rounded bg-red-50 px-1.5 py-0.5 text-xs text-red-700 line-through decoration-red-300">{String(c.from)}</span>
              <span className="text-slate-400">→</span>
              <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-xs font-semibold text-emerald-700">{String(c.to)}</span>
            </div>
          ))}
        </div>
      )}

      <div>
        <div className="px-4 pt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{isUpdate ? "Request body" : "Fields"}</div>
        <table className="w-full text-sm">
          <tbody className="divide-y divide-slate-50">
            {entries.map(([k, v]) => (
              <tr key={k}>
                <td className="w-2/5 px-4 py-1.5 align-top font-mono text-xs text-slate-500">{k}</td>
                <td className="px-4 py-1.5 align-top"><FieldValue value={v} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>)}
    </div>
  );
}

/**
 * useState that survives a page refresh by mirroring to localStorage. `allowed` guards against a stale
 * value left over from an older app version (these filters feed a tRPC enum query — an unknown value
 * would make the query throw), falling back to `initial` when the saved value is no longer valid.
 */
function usePersistentState<T extends string>(key: string, initial: T, allowed: readonly T[]): [T, (v: T) => void] {
  const [val, setVal] = useState<T>(() => {
    try {
      const saved = window.localStorage.getItem(key) as T | null;
      return saved && allowed.includes(saved) ? saved : initial;
    } catch {
      return initial;
    }
  });
  const set = (v: T) => {
    setVal(v);
    try {
      window.localStorage.setItem(key, v);
    } catch {
      /* private mode / quota — keep the in-memory value */
    }
  };
  return [val, set];
}

export default function ChangeSet() {
  const params = useParams();
  const id = Number(params.id);
  const utils = trpc.useUtils();
  const [opStatus, setOpStatus] = usePersistentState<"all" | "pending" | "applied" | "failed" | "skipped" | "dry_run">(
    "pbs.changeset.opStatus",
    "all",
    ["all", "pending", "applied", "failed", "skipped", "dry_run"],
  );
  const [opType, setOpType] = usePersistentState<"all" | "create" | "update" | "delete">("pbs.changeset.opType", "all", ["all", "create", "update", "delete"]);
  const [change, setChange] = usePersistentState<"all" | "create" | "reactivate" | "discontinue" | "price" | "purchase" | "description" | "visibility" | "supplier" | "name" | "stock" | "ean" | "brand" | "category" | "weight" | "depositNotice">(
    "pbs.changeset.change",
    "all",
    ["all", "create", "reactivate", "discontinue", "price", "purchase", "description", "visibility", "supplier", "name", "stock", "ean", "brand", "category", "weight", "depositNotice"],
  );
  const [previewOp, setPreviewOp] = useState<OpLite | null>(null);
  const [livePost, setLivePost] = useState<{ op: OpLite; startedAt: number } | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [batchStartedAt, setBatchStartedAt] = useState<number | null>(null);
  const [batchHidden, setBatchHidden] = useState(false); // "Hide" the progress modal WITHOUT ending the batch
  // Free-text search (SKU / item code / product name). Debounced so typing doesn't refetch every keystroke.
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  useEffect(() => { const t = setTimeout(() => setSearch(searchInput.trim()), 300); return () => clearTimeout(t); }, [searchInput]);
  const { log } = useProgressStream();
  const q = trpc.reconcile.changeSet.useQuery({ id, opStatus, change, opType, search, page, pageSize }, { refetchInterval: 4000, placeholderData: keepPreviousData });
  // "Launching" = the apply was kicked off but the detached worker hasn't reported "running" yet (it
  // cold-starts + acquires the lock, ~a few seconds). We keep the button in "Starting…" and poll faster
  // during this window so the click reflects immediately instead of looking like nothing happened.
  const [launching, setLaunching] = useState(false);
  // Restart-proof apply progress (op-count + lock based) — survives reload, flags an interrupted apply.
  const applyStatusQ = trpc.reconcile.applyStatus.useQuery(
    { id },
    { refetchInterval: (query) => (query.state.data?.running ? 1500 : launching ? 1000 : query.state.data?.interrupted ? 6000 : false) },
  );
  const as = applyStatusQ.data;
  const csStatus = q.data?.changeSet?.status;
  // Once the worker reports running (or has processed something), the launch landed — drop "Starting…".
  // A set that's ENTIRELY capped ops has nothing to actually process (total/processed are both 0 by
  // design — see getApplyStatus), so it can finish in well under a poll interval without `running` or
  // `processed` ever ticking. sawApplying tracks "we've actually observed this launch's own 'applying'
  // status" so a status change AWAY from it is also a safe, fast, correct "it's done" signal — without
  // this gate, a stale pre-existing terminal status (present before this click) would clear it instantly.
  const [sawApplying, setSawApplying] = useState(false);
  useEffect(() => { if (!launching) { setSawApplying(false); return; } if (csStatus === "applying") setSawApplying(true); }, [launching, csStatus]);
  const [flash, setFlash] = useState<{ msg: string; ok: boolean } | null>(null);
  const showFlash = (msg: string, ok = true) => { setFlash({ msg, ok }); setTimeout(() => setFlash(null), 5000); };
  useEffect(() => {
    if (!launching) return;
    const done = as?.running || (as?.processed ?? 0) > 0 || (sawApplying && csStatus && csStatus !== "applying");
    if (!done) return;
    setLaunching(false);
    // Nothing applied AND nothing failed, but ops are sitting capped: "Apply live" correctly did nothing,
    // by design (a blanket apply must never auto-post ops flagged for individual review) -- but from the
    // user's side that looks identical to the button being broken. Say so explicitly.
    if (as && as.applied === 0 && as.failed === 0 && as.cappedPending > 0) {
      showFlash(`Nothing to apply automatically — all ${as.cappedPending} operation(s) here are flagged for individual review (Safety review) and must be applied one by one or in a chosen batch.`, false);
    }
  }, [launching, as?.running, as?.processed, as?.applied, as?.failed, as?.cappedPending, sawApplying, csStatus]);
  // Safety net: never leave the button stuck on "Starting…" if the worker never reports back.
  useEffect(() => { if (!launching) return; const t = setTimeout(() => setLaunching(false), 30_000); return () => clearTimeout(t); }, [launching]);
  // Live SSE event for THIS set's apply only (stamped with changeSetId) — so two concurrent applies
  // never show each other's progress. Falls back to the polled op-count bar when no live event yet.
  const liveApplyEv = as?.running ? [...log].reverse().find((e) => e.job === "apply" && e.changeSetId === id) : undefined;
  const sys = trpc.system.info.useQuery(undefined, { refetchInterval: 15000 });
  const liveMode = sys.data?.writeMode === "live";
  // Effective mode for the badge — reflect what's actually HAPPENING, not the creation-time flag:
  // real posts → LIVE; only simulated ops → dry-run; nothing applied yet → the current global mode.
  const effLive = as && as.applied > 0 ? true : as && as.dryRunCount > 0 ? false : liveMode;
  const [selectingAll, setSelectingAll] = useState(false);
  // The plan that posting `previewOp` WOULD send right now (fresh-rebuilt for creates — shows step 3).
  const opPreviewQ = trpc.reconcile.opPreview.useQuery({ opId: previewOp?.id ?? 0 }, { enabled: !!previewOp });
  // The configured per-minute write budget (Settings) drives the time estimate — not a hardcoded rate.
  const cfgQ = trpc.config.get.useQuery();
  // For a Lightspeed set: is the ExactOnline side still unfinished? The LS plan is built from ExactOnline
  // data, so we warn before approving/applying if ExactOnline changes are still pending (apply those
  // first, then re-reconcile on fresh data).
  const isLsSet = q.data?.changeSet?.target === "lightspeed";
  const exactReadyQ = trpc.reconcile.exactReadiness.useQuery(undefined, { enabled: isLsSet, refetchInterval: 8000 });
  // A selection is only meaningful within the current filtered view — clear it when filters change so
  // we never post a stale/empty set (the selected ids may no longer be in view).
  useEffect(() => { setSelected(new Set()); setPage(1); }, [opStatus, change, opType, search]);
  // Once an op reaches "applied" (or otherwise stops being postable) the polled list reflects it —
  // drop it from the selection so its checkbox clears itself as the batch completes. Only prune ops
  // that are ON THE CURRENT PAGE (we can't see other pages' status here) so a cross-page "Select all"
  // selection isn't wiped on every refetch.
  useEffect(() => {
    const onPageDone = new Set((q.data?.operations ?? []).filter((o) => o.status !== "pending" && o.status !== "failed").map((o) => o.id));
    if (onPageDone.size === 0) return;
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set([...prev].filter((id) => !onPageDone.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [q.data]);
  // Snap back into range if a shrinking result set (ops applied) leaves us past the last page.
  // Snap back into range only when we actually have a page count. (Don't read `?? 1`: during page
  // navigation the query refetches under a new key and q.data is briefly undefined — clamping to 1
  // then would bounce you back to page 1 every time you hit Next.)
  useEffect(() => { const pc = q.data?.pageCount; if (pc != null && page > pc) setPage(pc); }, [q.data?.pageCount, page]);
  // The `change` filter persists across change sets (localStorage), but its valid values differ by
  // target (e.g. "create" is ExactOnline-only, "visibility" is LS-only). A leftover value that the current
  // set's dropdown can't render would show "all changes" while still filtering server-side to 0 rows
  // (this is why a Lightspeed set looked empty). Reset it to "all" when it doesn't apply here.
  const csTarget = q.data?.changeSet?.target;
  useEffect(() => {
    if (!csTarget) return;
    const ok = csTarget === "lightspeed" ? ["all", "create", "price", "purchase", "visibility", "supplier", "name", "stock", "ean", "brand", "category", "weight", "depositNotice"] : ["all", "create", "reactivate", "discontinue", "price", "purchase", "description"];
    if (!ok.includes(change)) setChange("all");
  }, [csTarget, change]);

  const approve = trpc.reconcile.approve.useMutation({
    onSuccess: () => {
      utils.reconcile.changeSet.invalidate();
      showFlash("Change set approved ✓ — it can be applied now");
    },
    onError: (e: { message: string }) => showFlash(`Approve failed: ${e.message}`, false),
  });
  const apply = trpc.reconcile.applySet.useMutation({
    onSuccess: () => {
      setLaunching(true);
      utils.reconcile.changeSet.invalidate();
      utils.reconcile.applyStatus.invalidate({ id });
      // Say it started. The progress block appears a moment later, and until then the click looked
      // like it had done nothing at all.
      const pending = as?.pending ?? 0;
      const failed = as?.failed ?? 0;
      const what = failed > 0 ? `${pending.toLocaleString("nl-NL")} pending + ${failed.toLocaleString("nl-NL")} previously failed` : `${pending.toLocaleString("nl-NL")} operation(s)`;
      showFlash(`${liveMode ? "Apply" : "Dry-run"} started — ${what}. It keeps running if you leave this page.`);
    },
    onError: (e) => { setLaunching(false); showFlash(`Could not start: ${e.message}`, false); },
  });
  const pauseApply = trpc.reconcile.pauseApply.useMutation({
    onSuccess: () => {
      utils.reconcile.applyStatus.invalidate({ id });
      // The worker finishes the item it's on, so "paused" isn't instant — say that instead of leaving
      // the button looking like it did nothing.
      showFlash("Pause requested — it stops after the operation it's on. The rest stays pending and can be resumed.");
    },
    onError: (e: { message: string }) => showFlash(`Pause failed: ${e.message}`, false),
  });
  const cancel = trpc.reconcile.cancel.useMutation({
    onSuccess: (r) => {
      utils.reconcile.changeSet.invalidate();
      utils.reconcile.applyStatus.invalidate({ id });
      showFlash(
        `Change set #${id} cancelled — ${r.skipped.toLocaleString("nl-NL")} pending operation${r.skipped === 1 ? "" : "s"} skipped` +
          (r.keptApplied > 0 ? `, ${r.keptApplied.toLocaleString("nl-NL")} already-applied kept for the record` : ""),
      );
    },
    onError: (e: { message: string }) => showFlash(`Cancel failed: ${e.message}`, false),
  });
  const deleteSet = trpc.reconcile.deleteSet.useMutation({ onSuccess: () => { window.location.href = "/reconcile"; } });
  const applyOp = trpc.reconcile.applyOp.useMutation({
    onSuccess: () => utils.reconcile.changeSet.invalidate(),
    onError: () => utils.reconcile.changeSet.invalidate(), // error shown in the live modal
  });
  const applyOpsLive = trpc.reconcile.applyOpsLive.useMutation({
    onSuccess: () => utils.reconcile.changeSet.invalidate(),
    onError: (e) => { alert(`Batch post failed to start:\n${e.message}`); setBatchStartedAt(null); },
  });
  const deleteOp = trpc.reconcile.deleteOp.useMutation({
    onSuccess: () => { utils.reconcile.changeSet.invalidate(); utils.reconcile.applyStatus.invalidate({ id }); },
    onError: (e) => alert(e.message),
  });

  if (q.isLoading) return <Spinner label="Loading change set…" />;
  // A failed refetch leaves q.data undefined, which used to render as "not found" — a blank-looking page
  // for what is really a network/server error. Tell the difference, and offer a way back.
  if (q.isError) {
    return (
      <div className="space-y-2">
        <p className="text-sm font-medium text-red-700">Could not load this change set.</p>
        <p className="max-w-2xl text-xs text-slate-600">{q.error?.message ?? "Unknown error"}</p>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => q.refetch()}>{q.isFetching ? "Retrying…" : "Try again"}</Button>
          <Link href="/reconcile" className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50">Back to change sets</Link>
        </div>
      </div>
    );
  }
  const cs = q.data?.changeSet;
  if (!cs) return <p className="text-sm text-slate-500">Change set not found. <Link href="/reconcile" className="text-blue-600">Back</Link></p>;

  const s = (parseMaybe(cs.summary) ?? {}) as { total?: number; create?: number; update?: number; delete?: number; maxPriceDeltaPct?: number; lsHideSkipReason?: string | null };
  // Warn before posting a Lightspeed set while ExactOnline work is still pending (the LS plan is built
  // from ExactOnline data). Not a hard block — the user can proceed if they know what they're doing.
  const exactBlocking = cs.target === "lightspeed" && !!exactReadyQ.data?.blocking;
  const exactWarnMsg = exactReadyQ.data?.message ?? "";
  const confirmExactReady = () => !exactBlocking || confirm(`⚠️ ${exactWarnMsg}\n\nApply this Lightspeed change set anyway?`);
  const ops = q.data?.operations ?? []; // the current PAGE, filtered server-side by status/change/type
  const total = q.data?.total ?? ops.length; // true count for the filter, across all pages
  const pageCount = q.data?.pageCount ?? 1;
  const canApprove = cs.status === "pending_approval";
  // Resumable when approved/failed/partially-failed, or interrupted mid-apply (stuck "applying" with no
  // live worker). Hidden while an apply is actively running (the lock would reject a second run anyway).
  const isRunning = !!as?.running;
  // An empty change set (reconcile found nothing to do) has no apply/cancel semantics — the only
  // sensible action is to delete it. Gate the action buttons on this so they aren't meaningless.
  const isEmpty = (s?.total ?? total) === 0 && !isRunning;
  const canApply = !isRunning && !isEmpty && (["approved", "pending_approval", "partially_failed", "failed"].includes(cs.status) || !!as?.interrupted);
  const filtersActive = change !== "all" || opType !== "all" || opStatus !== "all" || !!search;
  const clearFilters = () => { setChange("all"); setOpType("all"); setOpStatus("all"); setSearchInput(""); setSearch(""); };
  // Rough API-call estimate per op (idempotency reads + writes + read-back verify) so the user can
  // gauge budget/time before a batch. Create ≈ 7 ExactOnline calls; ExactOnline update ≈ 4; LS update ≈ 2.
  const estCalls = (list: typeof ops) => list.reduce((n, o) => n + (o.opType === "create" ? 7 : o.target === "lightspeed" ? 2 : 4), 0);
  // Selection can span pages (Select all), so estimate from the count × the current page's average cost.
  const avgCalls = ops.length ? estCalls(ops) / ops.length : cs.target === "lightspeed" ? 2 : 4;
  const estSelectedCalls = Math.round(selected.size * avgCalls);
  // Per-minute write budget for THIS target, from Settings (budget.exactPerMinute / budget.lsPerMinute).
  const cfgScalars = (cfgQ.data?.scalars ?? []) as { key: string; value: unknown }[];
  const cfgNum = (key: string, fallback: number) => { const n = Number(cfgScalars.find((s) => s.key === key)?.value); return Number.isFinite(n) && n > 0 ? n : fallback; };
  const perMin = cs.target === "lightspeed" ? cfgNum("budget.lsPerMinute", 55) : cfgNum("budget.exactPerMinute", 50);
  const perDay = cs.target === "lightspeed" ? cfgNum("budget.lsPerDay", 12000) : cfgNum("budget.exactPerDay", 5000);
  const estMinutes = Math.max(1, Math.ceil(estSelectedCalls / perMin));

  const postOneLive = (op: OpLite) => {
    const reposting = op.status === "applied";
    const what =
      op.opType === "create"
        ? op.target === "lightspeed"
          ? "create the product on Lightspeed (variant SKU/price/EAN/stock, category, brand, images)"
          : "create the item + supplier link and set its sales price"
        : `${op.opType} ${op.entityKey}`;
    if (!confirmExactReady()) return;
    const msg = reposting
      ? `Re-post this operation LIVE to ${op.target.toUpperCase()}?\n\nThe record already exists — this RE-SYNCS it (e.g. fills a missing sales price) without creating duplicates. Continue?`
      : `Post this 1 operation LIVE to ${op.target.toUpperCase()}?\n\nThis will ${what} for real — regardless of the global dry-run setting. Continue?`;
    if (!confirm(msg)) return;
    setLivePost({ op, startedAt: Date.now() });
    applyOp.reset();
    applyOp.mutate({ opId: op.id });
  };
  // Live step log for the in-progress post (from the SSE stream, job "applyOp", since we started).
  const liveSteps = livePost ? log.filter((e) => e.job === "applyOp" && e.at >= livePost.startedAt - 500) : [];

  // ── multi-select / batch post ──
  const allSelected = ops.length > 0 && ops.every((o) => selected.has(o.id));
  const toggleOne = (opId: number) =>
    setSelected((s) => {
      const n = new Set(s);
      n.has(opId) ? n.delete(opId) : n.add(opId);
      return n;
    });
  // Header checkbox = select/deselect the CURRENT PAGE only.
  const toggleAll = () =>
    setSelected((s) => {
      const n = new Set(s);
      if (allSelected) ops.forEach((o) => n.delete(o.id));
      else ops.forEach((o) => n.add(o.id));
      return n;
    });
  // "Select all N matching" = fetch EVERY matching op id (across all pages) and select them.
  const selectAllMatching = async () => {
    setSelectingAll(true);
    try {
      const ids = await utils.reconcile.opIds.fetch({ id, opStatus, change, opType, search });
      setSelected(new Set(ids));
    } catch (e) {
      alert(`Could not select all: ${e instanceof Error ? e.message : e}`);
    } finally {
      setSelectingAll(false);
    }
  };
  // No -500 buffer here (unlike the single-op log): the server's "start"/"op"/"done" events all arrive
  // AFTER we set batchStartedAt, so an earlier batch's "done" can't leak into this one's view.
  const batchSteps = batchStartedAt !== null ? log.filter((e) => e.job === "applyBatch" && e.at >= batchStartedAt) : [];
  const batchDone = batchSteps.some((e) => e.step === "done");
  const batchRunning = batchStartedAt !== null && !batchDone;
  const postSelectedLive = () => {
    const ids = [...selected]; // ALL selected ids — across pages, not just the current page
    if (!ids.length) return;
    if (!confirmExactReady()) return;
    const est = estSelectedCalls;
    if (!confirm(`Post ${ids.length} operation${ids.length === 1 ? "" : "s"} LIVE to ${cs!.target.toUpperCase()}?\n\n≈ ${est} API calls — paced at your configured ~${perMin}/min budget, so ~${Math.max(1, Math.ceil(est / perMin))} min in the background (daily cap ${perDay.toLocaleString()}). The MCP proxy's real throughput varies. Each is sent for real regardless of the global dry-run setting. Failures are recorded and don't stop the rest.\n\nContinue?`)) return;
    setBatchStartedAt(Date.now());
    setBatchHidden(false);
    applyOpsLive.mutate({ id, opIds: ids });
  };

  // The plan + prior-apply results come from opPreview (the heavy json columns are no longer in the
  // polled list). For ExactOnline creates this plan is rebuilt fresh — so it includes the step-3 sales price
  // that old 2-step ops never stored.
  const freshPreview = opPreviewQ.data as { steps?: Step[]; warnings?: string[]; rebuilt?: boolean; done?: { skipped?: boolean }[]; stored?: { steps?: Step[]; warnings?: string[] } | null; view?: { exact?: string; lightspeed?: string } } | null | undefined;
  const previewView = freshPreview?.view;
  const steps = freshPreview?.steps as Step[] | undefined;
  const warnings = freshPreview?.warnings ?? [];
  const previewLoading = !!previewOp && opPreviewQ.isLoading;
  const isCreate = previewOp?.opType === "create";
  const previewDiff = previewOp ? ((parseMaybe(previewOp.diff) ?? {}) as { itemCode?: string }) : {};
  // What already happened on a prior apply: applied → all steps done; failed → partial.steps done, next failed.
  const doneResults = freshPreview?.done ?? [];
  const completedCount = Array.isArray(doneResults) ? doneResults.length : 0;
  const failedIdx = previewOp?.status === "failed" ? completedCount : -1;
  const stepState = (i: number): "done" | "skipped" | "failed" | "pending" | undefined => {
    if (i < completedCount) return doneResults[i]?.skipped ? "skipped" : "done";
    if (i === failedIdx) return "failed";
    return completedCount > 0 || failedIdx >= 0 ? "pending" : undefined;
  };

  return (
    <div className="space-y-6">
      {flash && (
        <div className={`fixed bottom-5 right-5 z-50 max-w-sm rounded-lg px-4 py-2.5 text-sm font-medium shadow-lg ${flash.ok ? "bg-emerald-600 text-white" : "bg-amber-600 text-white"}`}>
          {flash.msg}
        </div>
      )}
      <div className="flex items-center justify-between">
        <div>
          <Link href="/reconcile" className="text-sm text-blue-600">← Reconcile</Link>
          <h1 className="flex flex-wrap items-center gap-2 text-xl font-semibold">
            Change set #{cs.id}
            {/* Prominent, colour-coded target so it's unmistakable WHICH system this set writes to. */}
            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold ${cs.target === "lightspeed" ? "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300" : "bg-indigo-100 text-indigo-800 ring-1 ring-indigo-300"}`}>
              {cs.target === "lightspeed" ? "🛒 Lightspeed (webshop)" : "📒 ExactOnline"}
              <span className="font-normal opacity-70">· {cs.target === "lightspeed" ? "ExactOnline → Lightspeed" : "PBS → ExactOnline"}</span>
            </span>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {effLive ? <Badge color="red">LIVE</Badge> : <Badge color="blue">dry-run</Badge>}
          {/* "partially_failed" is ALSO the status for a set that has zero real failures but N items
              held back on Safety review (see apply.ts's finalStatus) — showing that raw label sends you
              looking for a "failed" filter that will always come back empty. Relabel that specific case. */}
          {(() => {
            const cappedOnly = cs.status === "partially_failed" && (as?.failed ?? 0) === 0 && (as?.cappedPending ?? 0) > 0;
            const label = cappedOnly ? "needs review" : cs.status;
            const color = cs.status === "applied" ? "green" : cappedOnly ? "amber" : cs.status.includes("fail") ? "red" : cs.status === "pending_approval" ? "amber" : "gray";
            return <Badge color={color}>{label}</Badge>;
          })()}
        </div>
      </div>

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-slate-600">
            {/* Always mounted, only its visibility changes. Mounting/unmounting a node on every 4-second
                poll is the one thing React cannot survive if anything else touches the DOM — and this
                span sat exactly where the insertBefore crash landed. */}
            <span className={`mr-2 text-[11px] font-medium text-indigo-600 transition-opacity ${q.isFetching ? "opacity-100" : "opacity-0"}`} aria-hidden={!q.isFetching}>
              refreshing…
            </span>
            {s?.total ?? 0} operations · {s?.create ?? 0} create · {s?.update ?? 0} update{(s?.delete ?? 0) > 0 ? ` · ${s.delete} delete` : ""}
            {" · "}created {fmtDateTime(cs.createdAt)}
            {cs.requiresApproval && cs.status === "pending_approval" && <span className="ml-2"><Badge color="amber">approval required</Badge></span>}
            {cs.status === "approved" && <span className="ml-2"><Badge color="green">approved ✓</Badge></span>}
          </div>
          <div className="flex gap-2">
            {canApprove && (
              <Button disabled={approve.isPending} onClick={() => { if (confirmExactReady()) approve.mutate({ id }); }}>
                {approve.isPending ? "Approving…" : "Approve"}
              </Button>
            )}
            {isRunning && (
              <Button
                variant="secondary"
                disabled={pauseApply.isPending}
                onClick={() => pauseApply.mutate({ id })}
                title="Pause after the current item — the rest stays pending and you can resume any time"
              >
                {pauseApply.isPending ? "Pausing…" : "⏸ Pause"}
              </Button>
            )}
            {canApply && (
              <Button
                variant={liveMode ? "danger" : "secondary"}
                disabled={apply.isPending || launching}
                onClick={() => { if (confirmExactReady()) apply.mutate({ id }); }}
                title={liveMode ? "Send these changes to the live system" : "Simulate — records what would be sent, posts nothing"}
              >
                {apply.isPending || launching
                  ? "Starting…"
                  : as?.interrupted || (as && as.processed > 0 && as.pending > 0)
                    ? liveMode ? "Resume live" : "Resume (dry-run)"
                    : liveMode ? "Apply live" : "Run dry-run"}
              </Button>
            )}
            {!isEmpty && cs.status !== "cancelled" && cs.status !== "applied" && (
              <Button
                variant="ghost"
                disabled={cancel.isPending}
                title="Close this plan: everything still pending is marked skipped and won't be posted. Applied operations stay on the record."
                onClick={() => {
                  const pending = as?.pending ?? null;
                  const n = pending != null ? `${pending.toLocaleString("nl-NL")} pending operation(s)` : "the operations still pending";
                  if (
                    confirm(
                      `Cancel change set #${id}?\n\n` +
                        `${n} will be marked skipped and never posted. Anything already applied stays as it is, and the set stays visible as history.\n\n` +
                        `This cannot be undone — a new reconcile builds a fresh plan of whatever still differs.`,
                    )
                  ) {
                    cancel.mutate({ id });
                  }
                }}
              >
                {cancel.isPending ? "Cancelling…" : "Cancel"}
              </Button>
            )}
            <Button variant="danger" onClick={() => { if (confirm(`Delete change set #${id}? This removes its planned operations and cannot be undone.`)) deleteSet.mutate({ id }); }}>Delete</Button>
          </div>
        </div>
        {exactBlocking && !isEmpty && (
          <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
            <span className="font-semibold">⚠ Finish the ExactOnline side first.</span> {exactWarnMsg}
          </div>
        )}
        {/* Warn BEFORE the click, not just after: a set that's entirely capped ops has nothing for the
            blanket button to actually do — clicking it would look identical to "broken" (no rows change,
            badge stays the same) unless this is said up front. */}
        {canApply && as && as.total === 0 && as.cappedPending > 0 && !isRunning && !launching && (
          <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
            <span className="font-semibold">⚠ Nothing for "{liveMode ? "Apply live" : "Run dry-run"}" to do.</span> All {as.cappedPending} operation(s) in this set are flagged for individual review — select them below (checkboxes) to post one by one or in a chosen batch.
          </div>
        )}
        {s.lsHideSkipReason && (
          <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
            <span className="font-semibold">⚠ Some hides were skipped.</span> {s.lsHideSkipReason}
          </div>
        )}
        {isEmpty && (
          <p className="mt-2 text-xs text-slate-600">
            Nothing to apply — this reconcile found <span className="font-medium">no differences</span>, so PBS and{" "}
            {cs.target === "lightspeed" ? "Lightspeed" : "ExactOnline"} are already in sync. You can safely{" "}
            <span className="font-medium">delete</span> this empty change set.
          </p>
        )}
        {!isEmpty && cs.requiresApproval && cs.status === "pending_approval" && (
          <p className="mt-2 text-xs text-amber-600">This change set must be <span className="font-medium">approved</span> first (button above), then applied.</p>
        )}
        {/* When the app is in dry-run mode, the only button is "Run dry-run" (simulate) — make the path
            to a real apply obvious, since that's the #1 confusion ("where is the Apply button?"). */}
        {!liveMode && canApply && (
          <p className="mt-2 text-xs text-slate-600">
            The app is in <span className="font-medium">dry-run</span> mode, so the button only{" "}
            <span className="font-medium">simulates</span> (nothing is sent). To apply these {(s?.total ?? total).toLocaleString()} change{(s?.total ?? total) === 1 ? "" : "s"} to{" "}
            {cs.target === "lightspeed" ? "Lightspeed" : "ExactOnline"} for real, switch to <span className="font-medium">LIVE</span> in the top banner — the button then becomes{" "}
            <span className="font-medium text-red-600">Apply live</span>.
          </p>
        )}
        {/* Restart-proof apply progress — works for ExactOnline AND Lightspeed, survives a reload, shows when an
            apply was interrupted or paused so it can be resumed. Live SSE (stamped with this change set's
            id) refines it while a worker runs; never shows another set's progress. */}
        {as && (as.running || as.interrupted || as.paused || (as.processed > 0 && as.pending > 0) || cs.status === "applying") && (
          <div className={`mt-3 rounded-lg border px-3 py-2.5 ${as.interrupted || as.paused ? "border-amber-300 bg-amber-50" : "border-indigo-200 bg-indigo-50/50"}`}>
            <div className={`mb-1.5 text-xs font-semibold ${as.interrupted || as.paused ? "text-amber-800" : "text-indigo-800"}`}>
              {as.running
                ? `Applying the whole set… (${as.target === "lightspeed" ? "Lightspeed" : "ExactOnline"})${as.lanes ? ` — ${as.lanes} ${as.lanes === 1 ? "operation" : "operations"} at a time` : ""}`
                : as.budgetPaused
                  ? "Paused — daily API budget reached"
                  : as.paused
                    ? "Paused — resume to finish"
                    : as.interrupted
                      ? "Apply was interrupted — resume to finish"
                      : as.pending > 0
                        ? "Partially applied"
                        : "Apply complete"}
            </div>
            {/* The trap that costs a run: lanes are fixed when Apply/Resume is pressed, so raising the
                setting mid-run changes nothing until you pause and resume. Say so, don't make them guess. */}
            {as.running && as.lanes != null && as.lanesConfigured != null && as.lanesConfigured !== as.lanes && (
              <div className="mb-1.5 text-[11px] text-amber-800">
                Settings now say <span className="font-semibold">{as.lanesConfigured}</span> at a time, but this run started on{" "}
                <span className="font-semibold">{as.lanes}</span> — the lane count is fixed when the apply starts.
                Pause and resume to pick up the new value.
              </div>
            )}
            {/* Prefer the live stream while it has events for this run; otherwise the durable view,
                which is also what a page reload falls back to. */}
            {as.running && liveApplyEv && as.retrying === 0 ? (
              <JobProgress ev={liveApplyEv} />
            ) : (
              <ApplyProgress as={as} system={as.target === "lightspeed" ? "Lightspeed" : "ExactOnline"} />
            )}
            {as.budgetPaused
              ? <p className="mt-1.5 text-[11px] text-amber-700">The daily {as.target === "lightspeed" ? "Lightspeed" : "ExactOnline"} API budget (Settings → Budgets) is used up. The remaining {as.pending.toLocaleString("nl-NL")} will continue <span className="font-medium">automatically when the budget resets</span> (after midnight). You can also raise the budget or click {liveMode ? "“Resume live”" : "“Resume”"} to continue now — it never re-posts what already went out (idempotent).</p>
              : (as.interrupted || as.paused) && <p className="mt-1.5 text-[11px] text-amber-700">Click {liveMode ? "“Resume live”" : "“Resume”"} above — it continues from the {as.pending.toLocaleString("nl-NL")} remaining and never re-posts what already went out (idempotent).</p>}
          </div>
        )}
      </Card>

      <Card title={`${cs.target === "lightspeed" ? "🛒 Lightspeed" : "📒 ExactOnline"} operations (${total.toLocaleString()})`} actions={
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search SKU / item code / name…"
            title="Search by supplier (PBS) SKU, ExactOnline item code, Lightspeed article code, or the product name (PBS / ExactOnline / Lightspeed)"
            className={`w-56 rounded-md border px-2 py-1 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-500 ${search ? "border-amber-400 ring-2 ring-amber-300" : "border-slate-300"}`}
          />
          {filtersActive && <button onClick={clearFilters} className="rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100" title="Clear all filters and show the full list">✕ clear filters</button>}
          <Select value={change} onChange={(e) => setChange(e.target.value as typeof change)} title="Filter by what the operation changes" className={change !== "all" ? "border-amber-400 ring-2 ring-amber-300" : ""}>
            <option value="all">all changes</option>
            <option value="create">{cs.target === "lightspeed" ? "create product" : "create item"}</option>
            {cs.target !== "lightspeed" && <option value="reactivate">reactivate</option>}
            {cs.target !== "lightspeed" && <option value="discontinue">discontinue</option>}
            <option value="price">sales price</option>
            <option value="purchase">purchase price</option>
            {cs.target !== "lightspeed" && <option value="description">description</option>}
            {cs.target !== "exact" && <option value="visibility">visibility</option>}
            {cs.target !== "exact" && <option value="supplier">supplier</option>}
            {cs.target !== "exact" && <option value="name">name</option>}
            {cs.target !== "exact" && <option value="stock">stock</option>}
            {cs.target !== "exact" && <option value="ean">EAN</option>}
            {cs.target !== "exact" && <option value="brand">brand</option>}
            {cs.target !== "exact" && <option value="category">category</option>}
            {cs.target !== "exact" && <option value="weight">weight</option>}
            {cs.target !== "exact" && <option value="depositNotice">deposit notice</option>}
          </Select>
          <Select value={opType} onChange={(e) => setOpType(e.target.value as typeof opType)} className={opType !== "all" ? "border-amber-400 ring-2 ring-amber-300" : ""}>
            <option value="all">all types</option><option value="create">create</option><option value="update">update</option><option value="delete">delete</option>
          </Select>
          <Select value={opStatus} onChange={(e) => setOpStatus(e.target.value as typeof opStatus)} className={opStatus !== "all" ? "border-amber-400 ring-2 ring-amber-300" : ""}>
            <option value="all">all statuses</option><option value="pending">pending</option><option value="applied">applied</option>
            <option value="dry_run">dry-run</option><option value="failed">failed</option><option value="skipped">skipped</option>
          </Select>
          {/* placeholderData keeps the OLD rows on screen while a filter change refetches (so isLoading
              never flips true), which is why changing a filter looked unresponsive. isPlaceholderData is
              true for exactly that window — unlike isFetching, it's NOT also true during the routine 4s
              background poll on an unchanged filter, so this won't flicker constantly. */}
          {q.isFetching && q.isPlaceholderData && <Spinner />}
        </div>
      }>
        {/* Filter-active banner — make it impossible to mistake a filtered view for the full list. */}
        {filtersActive && (
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <span>
              <span className="font-semibold">Filtered view</span> — {total.toLocaleString()} matching of {(s?.total ?? total).toLocaleString()} total operations.
            </span>
            <button onClick={clearFilters} className="rounded-md border border-amber-300 bg-white px-2 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100">Clear filters</button>
          </div>
        )}
        {/* Bulk action bar — appears once rows are selected. Posts the selected ops LIVE in one batch. */}
        {selected.size > 0 && (
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
            <span className="text-sm font-medium text-red-800" title={`API calls ≠ steps. Each step is a check-then-write, and writes are read back to verify — so a create ≈ 7 ExactOnline calls, an ExactOnline update ≈ 4, a Lightspeed update ≈ 2. Time is a rough guide using your configured ${perMin}/min budget (Settings); the MCP proxy's real throughput varies.`}>
              {selected.size.toLocaleString()} selected · ≈ {estSelectedCalls.toLocaleString()} API calls (~{estMinutes} min at {perMin}/min)
              {selected.size < total && (
                <button onClick={selectAllMatching} disabled={selectingAll} className="ml-2 font-semibold text-indigo-700 underline hover:text-indigo-900">
                  {selectingAll ? "selecting…" : `Select all ${total.toLocaleString()}`}
                </button>
              )}
            </span>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setSelected(new Set())}>Clear</Button>
              <Button variant="danger" disabled={batchRunning || applyOpsLive.isPending} onClick={postSelectedLive}>
                Post {selected.size.toLocaleString()} LIVE
              </Button>
            </div>
          </div>
        )}
        <table className="w-full table-fixed text-left text-sm">
          <colgroup>
            <col className="w-8" /><col className="w-24" /><col className="w-52" /><col /><col className="w-24" /><col className="w-24" /><col className="w-32" /><col className="w-36" />
          </colgroup>
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-400">
            <tr>
              <th className="px-2 py-2"><input type="checkbox" checked={allSelected} onChange={toggleAll} title="Select/deselect this page (use 'Select all N' to span all pages)" className="cursor-pointer" /></th>
              <th className="px-3 py-2">Operation</th><th className="px-3 py-2">Item</th><th className="px-3 py-2">Change</th><th className="px-3 py-2">Target</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Request</th><th className="px-3 py-2">Test</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {ops.map((op) => (
              <tr key={op.id} className={`align-top hover:bg-slate-50 ${selected.has(op.id) ? "bg-red-50/40" : ""}`}>
                <td className="px-2 py-3"><input type="checkbox" checked={selected.has(op.id)} onChange={() => toggleOne(op.id)} className="cursor-pointer" /></td>
                <td className="px-3 py-3"><Badge color={op.opType === "create" ? "green" : op.opType === "delete" ? "red" : "blue"}>{op.opType}</Badge></td>
                <td className="px-3 py-3"><ItemCell op={op as OpLite} fallback={q.data?.metaByKey?.[op.entityKey]} /></td>
                <td className="px-3 py-3 text-sm">
                  <DiffCell op={op as OpLite} />
                  {op.lastError && op.status === "failed" && <div className="mt-1 max-h-20 overflow-auto break-words rounded bg-red-50 px-2 py-1 text-[11px] leading-snug text-red-700">{op.lastError}</div>}
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-xs text-slate-400">{op.target === "lightspeed" ? "Lightspeed" : "ExactOnline"}</td>
                <td className="px-3 py-3"><Badge color={op.status === "applied" ? "green" : op.status === "failed" ? "red" : op.status === "dry_run" ? "blue" : op.status === "skipped" ? "gray" : "amber"}>{op.status}</Badge></td>
                <td className="px-3 py-3">
                  <button onClick={() => setPreviewOp(op as OpLite)} className="whitespace-nowrap rounded-md border border-slate-200 px-2.5 py-1 text-xs text-indigo-600 hover:bg-indigo-50">
                    {op.method} · view
                  </button>
                </td>
                <td className="whitespace-nowrap p-3 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <button
                      onClick={() => postOneLive(op as OpLite)}
                      disabled={applyOp.isPending}
                      className={
                        op.status === "applied"
                          ? "whitespace-nowrap rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                          : "whitespace-nowrap rounded-md border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
                      }
                      title={op.status === "applied" ? "Re-post this operation to re-sync the live record (idempotent — fills a missing sales price, no duplicates)" : "Post just this one operation to the live system"}
                    >
                      {op.status === "applied" ? "Re-post" : "Post 1 LIVE"}
                    </button>
                    <button
                      onClick={() => { if (confirm(`Remove operation ${op.entityKey} (${op.opType} → ${op.target}) from this change set? This only removes it from the plan — it doesn't undo anything already applied.`)) deleteOp.mutate({ opId: op.id }); }}
                      disabled={deleteOp.isPending}
                      className="rounded-md border border-slate-200 px-2 py-1 text-xs leading-none text-slate-400 hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                      title="Remove this operation from the change set"
                    >
                      ✕
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {ops.length === 0 && <p className="py-6 text-center text-sm text-slate-400">No operations match this filter.</p>}
        {/* Pagination */}
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm text-slate-500">
          <span>{total.toLocaleString()} operation{total === 1 ? "" : "s"}{pageCount > 1 ? ` · page ${page} of ${pageCount}` : ""}</span>
          <div className="flex items-center gap-2">
            <Select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} title="Rows per page">
              {[50, 100, 200, 500].map((n) => <option key={n} value={n}>{n} / page</option>)}
            </Select>
            <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>← Prev</Button>
            <Button variant="secondary" size="sm" disabled={page >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>Next →</Button>
          </div>
        </div>
      </Card>

      {previewOp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setPreviewOp(null)}>
          <div className="max-h-[88vh] w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
              <h3 className="text-sm font-semibold text-slate-800">
                Request preview
                <span className="ml-2 select-all font-mono text-xs text-slate-500">{previewOp.entityKey}</span>
                {previewDiff.itemCode && (
                  <span className="ml-2 text-xs font-normal text-slate-400">→ ExactOnline item <span className="select-all font-mono text-slate-700">{previewDiff.itemCode}</span></span>
                )}
              </h3>
              <button onClick={() => setPreviewOp(null)} className="text-slate-400 hover:text-slate-700">✕</button>
            </div>
            <div className="max-h-[72vh] space-y-3 overflow-auto p-5">
              {warnings.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  {warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
                </div>
              )}
              {(previewView?.exact || previewView?.lightspeed) && (
                <div className="flex flex-wrap gap-3 rounded-lg bg-slate-50 px-3 py-2 text-xs">
                  {previewView?.exact && <a href={previewView.exact} target="_blank" rel="noopener noreferrer" className="font-medium text-indigo-600 underline hover:text-indigo-800">View in ExactOnline ↗</a>}
                  {previewView?.lightspeed && <a href={previewView.lightspeed} target="_blank" rel="noopener noreferrer" className="font-medium text-indigo-600 underline hover:text-indigo-800">View in Lightspeed ↗</a>}
                </div>
              )}
              {previewLoading && !steps ? (
                <Spinner label="Building the exact plan that will be posted…" />
              ) : steps ? (
                <>
                  <p className="text-xs text-slate-500">
                    {completedCount > 0
                      ? `Steps 1–${completedCount} already applied to ExactOnline${failedIdx >= 0 ? ` — it stopped at step ${failedIdx + 1}` : ""}. Re-posting skips the completed ones and resumes.`
                      : isCreate
                        ? cs.target === "lightspeed"
                          ? "This runs as sequential steps — create the product, set its default variant (SKU/price/EAN/stock), link the mapped category, find/create the brand, then upload the images. Idempotent: re-posting reuses an existing product."
                          : "This runs as sequential steps — the SupplierItem is linked to the Item created in step 1. All values are new."
                        : "Sequential steps. The highlighted old → new values show exactly what will change, and why."}
                  </p>
                  {steps.map((st, i) => <StepCard key={i} step={st} index={i} state={stepState(i)} />)}
                </>
              ) : (
                <div className="font-mono text-xs leading-relaxed">
                  <div className="mb-2 text-slate-500">{previewOp.method} {previewOp.endpoint}</div>
                  <SimpleTree data={freshPreview?.stored ?? null} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Live "Post 1 LIVE" progress — step-by-step over SSE */}
      {livePost && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
              <h3 className="text-sm font-semibold text-slate-800">
                Posting LIVE <span className="ml-1 select-all font-mono text-xs text-slate-500">{livePost.op.entityKey}</span>
                <span className="ml-2 text-xs font-normal text-slate-400">→ {livePost.op.target.toUpperCase()}</span>
              </h3>
              {!applyOp.isPending && <button onClick={() => setLivePost(null)} className="text-slate-400 hover:text-slate-700">✕</button>}
            </div>
            <div className="space-y-1.5 p-5">
              {liveSteps.length === 0 && applyOp.isPending && <div className="text-sm text-slate-500">Starting…</div>}
              {liveSteps.map((e, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <span className="mt-0.5">{e.percent < 0 ? "❌" : e.detail?.includes("✓") || e.step === "complete" ? "✅" : "⏳"}</span>
                  <span className={e.percent < 0 ? "text-red-700" : "text-slate-700"}>{e.detail || e.step}</span>
                </div>
              ))}
              {applyOp.isPending && (
                <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full bg-indigo-600 transition-all" style={{ width: `${Math.max(5, liveSteps.at(-1)?.percent ?? 5)}%` }} />
                </div>
              )}
              {applyOp.isSuccess && (
                <div className="mt-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700">
                  <div>Applied — verified ✓</div>
                  {(applyOp.data?.view?.exact || applyOp.data?.view?.lightspeed) && (
                    <div className="mt-1.5 flex flex-wrap gap-3 font-normal">
                      {applyOp.data?.view?.exact && <a href={applyOp.data.view.exact} target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline hover:text-indigo-800">View in ExactOnline ↗</a>}
                      {applyOp.data?.view?.lightspeed && <a href={applyOp.data.view.lightspeed} target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline hover:text-indigo-800">View in Lightspeed ↗</a>}
                    </div>
                  )}
                </div>
              )}
              {applyOp.isError && <div className="mt-2 break-words rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{applyOp.error.message}</div>}
            </div>
            {!applyOp.isPending && (
              <div className="flex justify-end border-t border-slate-100 px-5 py-3">
                <Button variant="secondary" onClick={() => setLivePost(null)}>Close</Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Batch "Post N LIVE" progress — overall progress over SSE (job "applyBatch") */}
      {batchStartedAt !== null && !batchHidden && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
              <h3 className="text-sm font-semibold text-slate-800">Posting batch LIVE <span className="ml-1 text-xs font-normal text-slate-400">→ {cs.target.toUpperCase()}</span></h3>
              {!batchRunning && <button onClick={() => { setBatchStartedAt(null); setSelected(new Set()); }} className="text-slate-400 hover:text-slate-700">✕</button>}
            </div>
            <div className="space-y-2 p-5">
              {batchSteps.at(-1) ? <JobProgress ev={batchSteps.at(-1)!} /> : <div className="text-sm text-slate-500">Starting…</div>}
              <p className="text-xs text-slate-400">Runs in the background — the table updates as each item completes. You can close this; posting continues.</p>
              {batchDone && <div className="mt-1 rounded-lg bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700">{batchSteps.at(-1)?.detail || "Done"} ✓</div>}
            </div>
            <div className="flex justify-end border-t border-slate-100 px-5 py-3">
              <Button variant="secondary" onClick={() => { if (batchRunning) { setBatchHidden(true); } else { setBatchStartedAt(null); setBatchHidden(false); } }}>{batchRunning ? "Hide" : "Close"}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Fallback tree for non-step previews (status/price updates, Lightspeed). */
function SimpleTree({ data, depth = 0 }: { data: unknown; depth?: number }) {
  if (data === null || data === undefined) return <span className="text-slate-400">null</span>;
  if (typeof data !== "object") {
    const t = typeof data;
    const cls = t === "number" ? "text-blue-700" : t === "boolean" ? "text-purple-700" : "text-emerald-700";
    return <span className={`font-semibold ${cls}`}>{t === "string" ? `"${data as string}"` : String(data)}</span>;
  }
  const entries = Array.isArray(data) ? data.map((v, i) => [String(i), v] as const) : Object.entries(data as Record<string, unknown>);
  if (entries.length === 0) return <span className="text-slate-400">{Array.isArray(data) ? "[ ]" : "{ }"}</span>;
  return (
    <div className={depth > 0 ? "ml-3 border-l border-slate-100 pl-3" : ""}>
      {entries.map(([k, v]) => (
        <div key={k} className="py-0.5">
          <span className="font-semibold text-slate-700">{k}</span>
          <span className="text-slate-400">: </span>
          <SimpleTree data={v} depth={depth + 1} />
        </div>
      ))}
    </div>
  );
}
